import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, Phone, CheckCircle2, FileText, Users, Award, ShieldCheck,
  ChevronRight, MessageCircle, Clock, IndianRupee, FileCheck, Sparkles, HelpCircle,
} from 'lucide-react';
import { services, getService, getRelated, megaMenuCategories } from '../data/serviceCatalog';
import useSEO from '../hooks/useSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotFound = () => (
  <div className="min-h-screen bg-white flex flex-col">
    <Header />
    <div className="flex-1 flex items-center justify-center px-4 pt-32 pb-24">
      <div className="max-w-md text-center">
        <div className="text-6xl mb-4">🔎</div>
        <h1 className="text-2xl font-black text-[#0A2463]">Service not found</h1>
        <p className="text-gray-600 mt-2">This service page may have moved. Browse the full catalog or contact us — we probably still handle it.</p>
        <div className="mt-6 flex gap-2 justify-center">
          <Link to="/services"><Button className="bg-[#0A2463] text-white">View all services</Button></Link>
          <Link to="/contact"><Button variant="outline">Contact us</Button></Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

const ServiceDetailPage = () => {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const svc = getService(serviceSlug);

  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  // SEO — must be before conditional return so hook ordering is stable
  useSEO({
    title: svc ? svc.metaTitle : 'Service — Utkarshudyam',
    description: svc ? svc.metaDescription : undefined,
    canonical: typeof window !== 'undefined' && svc ? `${window.location.origin}/services/${svc.slug}` : undefined,
    image: svc?.image,
    jsonLd: svc && {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: typeof window !== 'undefined' ? window.location.origin + '/' : '/' },
            { '@type': 'ListItem', position: 2, name: 'Services', item: typeof window !== 'undefined' ? window.location.origin + '/services' : '/services' },
            { '@type': 'ListItem', position: 3, name: svc.title },
          ],
        },
        {
          '@type': 'Service', name: svc.title, description: svc.metaDescription,
          provider: { '@type': 'Organization', name: 'Utkarshudyam Solution Private Limited' },
          areaServed: 'IN',
        },
        {
          '@type': 'FAQPage',
          mainEntity: (svc.faq || []).map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        },
      ],
    },
  });

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' }); }, [serviceSlug]);

  if (!svc) return <NotFound />;

  const Icon = svc.icon;
  const related = getRelated(svc.slug);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error('Please share your name and phone.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/enquiries`, {
        name: form.name, phone: form.phone, email: form.email || 'no-email@utkarshudyam.com',
        service: svc.title, message: form.message || `Enquiry for ${svc.title}`,
      });
      toast.success('Thanks! Our expert will reach out shortly.');
      setForm({ name: '', phone: '', email: '', message: '' });
    } catch (err) {
      toast.error('Could not submit — please try WhatsApp or phone.');
    } finally { setSubmitting(false); }
  };

  const categoryLabel = megaMenuCategories.find((c) => c.id === svc.category)?.label || 'Services';

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="pt-[116px] bg-gray-50">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center gap-1.5 text-xs text-gray-600 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#0A2463]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/services" className="hover:text-[#0A2463]">Services</Link>
            <ChevronRight size={12} />
            <span className="text-gray-500">{categoryLabel}</span>
            <ChevronRight size={12} />
            <span className="font-semibold text-[#0A2463]" data-testid="breadcrumb-current">{svc.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="py-14 bg-gradient-to-br from-[#0A2463] via-[#0d2d6e] to-[#123a8f] relative overflow-hidden">
        <div className="absolute -top-16 -right-10 w-96 h-96 bg-[#FFB703] opacity-10 blur-3xl rounded-full" />
        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white/90 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest mb-4">
                <Icon size={14} className="text-[#FFB703]" /> {categoryLabel}
              </div>
              <h1 className="text-3xl md:text-5xl font-black text-white leading-tight" data-testid="service-h1">{svc.title}</h1>
              <p className="text-lg md:text-xl text-[#FFB703] font-semibold mt-3">{svc.tagline}</p>
              <p className="text-gray-200 mt-4 text-sm md:text-base leading-relaxed">{svc.short}</p>

              <div className="mt-6 flex flex-wrap gap-3">
                <a href="#enquire">
                  <Button data-testid="hero-apply-btn" className="bg-gradient-to-r from-[#FFB703] to-[#FB8500] text-white text-base px-6 py-5 shadow-xl hover:scale-105 transition-all">
                    Apply Now <ArrowRight size={16} className="ml-1.5" />
                  </Button>
                </a>
                <Link to="/contact">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#0A2463] text-base px-6 py-5">
                    Get Free Consultation
                  </Button>
                </Link>
                <a href={`https://wa.me/919316085383?text=${encodeURIComponent('Hi, I am interested in ' + svc.title)}`} target="_blank" rel="noreferrer">
                  <Button className="bg-[#25d366] hover:bg-[#128c7e] text-white text-base px-6 py-5">
                    <MessageCircle size={16} className="mr-1.5" /> WhatsApp
                  </Button>
                </a>
              </div>

              <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
                {[[Clock, 'Timeline', svc.timeline], [IndianRupee, 'Pricing', svc.price], [FileCheck, 'Type', 'Managed']].map(([I, l, v]) => (
                  <div key={l} className="bg-white/5 border border-white/10 rounded-xl p-3">
                    <I size={14} className="text-[#FFB703]" />
                    <div className="text-[10px] uppercase text-white/60 font-bold mt-1">{l}</div>
                    <div className="text-xs font-semibold text-white mt-0.5 leading-tight">{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img src={svc.image} alt={svc.imageAlt} className="w-full h-[380px] object-cover" loading="eager" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A2463]/60 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview + Sticky enquiry */}
      <section className="py-14">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="lg:col-span-2 space-y-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-black text-[#0A2463]">Overview</h2>
                <p className="text-gray-700 mt-3 leading-relaxed">{svc.overview}</p>
                {svc.whoNeeds && (
                  <div className="mt-4 bg-[#FFF7EC] border border-[#F0DFC1] rounded-xl p-4">
                    <div className="text-xs font-bold uppercase text-[#FB8500] tracking-wider">Who Needs This?</div>
                    <p className="text-sm text-gray-700 mt-1">{svc.whoNeeds}</p>
                  </div>
                )}
              </div>

              {/* Benefits */}
              <div>
                <h2 className="text-2xl md:text-3xl font-black text-[#0A2463]">Key Benefits</h2>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  {svc.benefits.map((b) => (
                    <div key={b.title} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-bold text-[#0A2463] text-sm">{b.title}</div>
                          <div className="text-xs text-gray-600 mt-1 leading-relaxed">{b.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Eligibility */}
              {svc.eligibility?.length > 0 && (
                <div>
                  <h2 className="text-2xl md:text-3xl font-black text-[#0A2463]">Eligibility</h2>
                  <ul className="mt-4 space-y-2">
                    {svc.eligibility.map((e, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <Users size={16} className="text-[#0A2463] mt-0.5 flex-shrink-0" /> {e}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Documents */}
              <div>
                <h2 className="text-2xl md:text-3xl font-black text-[#0A2463]">Documents Required</h2>
                <div className="mt-4 bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                  <div className="grid sm:grid-cols-2 gap-2">
                    {svc.documents.map((d, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <FileText size={15} className="text-[#FB8500] mt-0.5 flex-shrink-0" /> {d}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Process timeline */}
              <div>
                <h2 className="text-2xl md:text-3xl font-black text-[#0A2463]">Our Process</h2>
                <div className="mt-4 relative border-l-2 border-[#FFB703]/30 pl-6 space-y-4">
                  {svc.process.map((p) => (
                    <div key={p.step} className="relative">
                      <div className="absolute -left-[33px] top-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#0A2463] to-[#123a8f] text-white text-xs font-bold flex items-center justify-center shadow">
                        {p.step}
                      </div>
                      <div className="text-sm font-bold text-[#0A2463]">{p.title}</div>
                      <div className="text-xs text-gray-600 mt-0.5">{p.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing */}
              <div className="rounded-2xl bg-gradient-to-r from-[#0A2463] to-[#123a8f] text-white p-6">
                <div className="flex items-center gap-2 text-[#FFB703] text-xs font-bold uppercase tracking-wider">
                  <IndianRupee size={14} /> Pricing
                </div>
                <div className="text-2xl md:text-3xl font-black mt-2">{svc.price}</div>
                <p className="text-sm text-white/80 mt-1">Final quote depends on your specific requirement. Get an exact figure in writing after a free 10-minute call.</p>
                <div className="mt-4">
                  <Link to="/contact">
                    <Button className="bg-[#FFB703] hover:bg-[#FB8500] text-white">Get Exact Quote <ArrowRight size={14} className="ml-1.5" /></Button>
                  </Link>
                </div>
              </div>

              {/* Why us */}
              <div>
                <h2 className="text-2xl md:text-3xl font-black text-[#0A2463]">Why choose Utkarshudyam?</h2>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  {[
                    { i: ShieldCheck, t: 'Expert Team', d: 'CAs, CS and industry experts on every case.' },
                    { i: FileCheck, t: 'End-to-End Docs', d: 'Checklists, drafts and prompt reminders.' },
                    { i: Award, t: 'DPIIT Certified', d: 'A recognised startup helping others grow.' },
                    { i: Sparkles, t: 'Transparent Fees', d: 'Written quotes, no surprises.' },
                  ].map((it) => (
                    <div key={it.t} className="bg-white border border-gray-100 rounded-xl p-4 flex items-start gap-3 shadow-sm">
                      <div className="w-9 h-9 rounded-lg bg-[#0A2463]/5 text-[#0A2463] flex items-center justify-center flex-shrink-0"><it.i size={18} /></div>
                      <div>
                        <div className="font-bold text-[#0A2463] text-sm">{it.t}</div>
                        <div className="text-xs text-gray-600 mt-0.5">{it.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              {svc.faq?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2">
                    <HelpCircle size={20} className="text-[#FB8500]" />
                    <h2 className="text-2xl md:text-3xl font-black text-[#0A2463]">Frequently Asked Questions</h2>
                  </div>
                  <div className="mt-4 bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
                    <Accordion type="single" collapsible className="w-full">
                      {svc.faq.map((f, i) => (
                        <AccordionItem key={i} value={`f${i}`} className="border-b border-gray-100 last:border-0">
                          <AccordionTrigger className="text-left hover:no-underline text-sm font-semibold text-[#0A2463]" data-testid={`faq-q-${i}`}>
                            {f.q}
                          </AccordionTrigger>
                          <AccordionContent className="text-sm text-gray-700 leading-relaxed pb-4">
                            {f.a}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </div>
              )}
            </div>

            {/* Sticky enquiry rail */}
            <aside className="lg:sticky lg:top-32 h-max" id="enquire">
              <div className="bg-white rounded-2xl border-2 border-[#FFB703]/40 shadow-2xl p-5">
                <div className="text-xs font-bold uppercase tracking-widest text-[#FB8500]">Talk to an Expert</div>
                <h3 className="text-lg font-black text-[#0A2463] mt-1">Free consultation for {svc.title}</h3>
                <form onSubmit={submit} className="mt-3 space-y-2.5" data-testid="service-enquiry-form">
                  <Input required placeholder="Your Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="enq-name" />
                  <Input required type="tel" placeholder="Mobile *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="enq-phone" />
                  <Input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="enq-email" />
                  <Textarea placeholder="Tell us what you need (optional)" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="enq-message" />
                  <Button type="submit" disabled={submitting} data-testid="enq-submit" className="w-full bg-gradient-to-r from-[#FFB703] to-[#FB8500] text-white">
                    {submitting ? 'Sending…' : 'Request Free Callback'}
                  </Button>
                </form>
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 text-center">
                  Or call <a href="tel:9755085383" className="font-bold text-[#0A2463]">9755085383</a>
                </div>
              </div>

              {related.length > 0 && (
                <div className="mt-5 bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-2">Related Services</div>
                  <ul className="space-y-1">
                    {related.map((r) => (
                      <li key={r.slug}>
                        <Link to={`/services/${r.slug}`} data-testid={`related-${r.slug}`}
                          className="group flex items-center gap-1 text-sm text-[#0A2463] hover:text-[#FB8500] py-1">
                          <ChevronRight size={13} className="text-[#FFB703]" /> {r.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-[#0A2463] via-[#0d2d6e] to-[#123a8f]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-4xl font-black text-white">Ready to get started with {svc.title}?</h2>
          <p className="text-gray-200 mt-2 text-sm md:text-base">Professional assistance from documentation to delivery.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <a href="#enquire"><Button className="bg-[#FFB703] hover:bg-[#FB8500] text-white text-base px-6 py-5">Apply Now</Button></a>
            <Link to="/contact"><Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#0A2463] text-base px-6 py-5">Talk to an Expert</Button></Link>
            <a href={`https://wa.me/919316085383?text=${encodeURIComponent('Hi, I am interested in ' + svc.title)}`} target="_blank" rel="noreferrer">
              <Button className="bg-[#25d366] hover:bg-[#128c7e] text-white text-base px-6 py-5"><Phone size={16} className="mr-1.5" /> WhatsApp</Button>
            </a>
          </div>
        </div>
      </section>

      {/* Back link */}
      <div className="container mx-auto px-4 py-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-[#0A2463] hover:text-[#FB8500]">
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
